import type { RefView } from "@minga/protocol"
import { useRouter } from "@tanstack/react-router"
import { type ReactNode, useCallback, useContext, useEffect, useId, useRef, useState } from "react"
import { Sheet } from "@/components/ui/sheet"
import { lookupOneRef } from "@/lib/refs-api"
import { usePhone } from "@/lib/use-media-query"
import { cn } from "@/lib/utils"
import { MonoRef } from "../refs/mono-ref"
import { MarkGlyph, RefIcon } from "../refs/ref-icons"
import { chipName, FloatingPanel, RefPeekBody, RefPreview, shownRef } from "../refs/ref-peek"
import { RefContext, RefDecideContext, RefLookupContext, type ResolvedRef, refTone } from "./refs"

const textOf = (node: ReactNode): string =>
  typeof node === "string" || typeof node === "number"
    ? String(node)
    : Array.isArray(node)
      ? node.map(textOf).join("")
      : ""

/** Hover and keyboard focus show the preview after this long (0136 §B). */
export const PREVIEW_DELAY_MS = 300

/** Refs a page's resolver may not know although the project has them (old runs, decided cards, decision comments). */
const LOOKUP_FALLBACK = /^[DPRWVO]-[1-9]\d*$/

/**
 * Decision 0144: a ref the page's resolver doesn't know, asked from the server once (a decision comment's D-n, a run
 * compacted out of the snapshot). Only inside a project context (`RefLookupContext`); only single-letter refs, so
 * "GPT-5" never costs a request.
 */
const useLookupFallback = (ref: string, enabled: boolean): RefView | null => {
  const project = useContext(RefLookupContext)
  const [view, setView] = useState<RefView | null>(null)
  const active = enabled && project !== null && LOOKUP_FALLBACK.test(ref)
  useEffect(() => {
    if (!active || !project) return
    let live = true
    void lookupOneRef(project.workspaceId, project.projectId, ref).then((v) => {
      if (live) setView(v)
    })
    return () => {
      live = false
    }
  }, [active, project, ref])
  return active ? view : null
}

/**
 * A Minga ref as a chip (refined-system §5.3: `fill`, radius 3, mono, keeps the line height), resolved through the surface's
 * {@link RefContext}; a ref the surface can't resolve stays plain text. Used inside markdown (`<minga-ref>`) and in
 * tool row labels. In the main chunk: no Streamdown here.
 *
 * Decision 0144 (experiment 11, "peek and act"): a resolved ref with a view is a real link (middle-click and Cmd-click
 * open its page) with its type icon and one status mark. Hover or keyboard focus shows a read-only preview after
 * 300 ms; a click, Enter or Space opens a popover (a bottom sheet on a phone) with the key facts and, for an open card,
 * the card itself to decide in place. Escape closes it and focus returns to the chip.
 */
export function RefChip({
  children,
  refText,
  plain = false,
}: {
  children?: ReactNode
  refText?: string | undefined
  /** Never a button: for chips inside another control (a row's `<summary>`), where a button would nest. */
  plain?: boolean | undefined
}) {
  const resolve = useContext(RefContext)
  const ref = (refText ?? textOf(children)).trim()
  const local = resolve?.(ref) ?? null
  const fallback = useLookupFallback(ref, local === null || local.view === undefined)
  const resolved: ResolvedRef | null =
    local?.view !== undefined
      ? local
      : fallback
        ? { ...(local ?? { tone: refTone(ref) }), view: fallback }
        : local
  if (!resolved) return <>{ref}</>
  if (resolved.view) return <PeekChip refText={ref} resolved={resolved} view={resolved.view} plain={plain} />
  return <LegacyChip refText={ref} resolved={resolved} plain={plain} />
}

/** A chip without a view (a surface that resolves only a title and an opener): as before 0144. */
function LegacyChip({
  refText,
  resolved,
  plain,
}: {
  refText: string
  resolved: ResolvedRef
  plain: boolean
}) {
  const className = cn(
    "mx-px inline-flex items-baseline gap-1.5 rounded-pill bg-fill px-1.5 py-px align-baseline font-mono text-[0.875em] leading-[inherit] whitespace-nowrap text-ink [box-decoration-break:clone]",
    resolved.open && !plain && "cursor-pointer hover:bg-fill-hover",
  )
  return resolved.open && !plain ? (
    <button
      type="button"
      className={className}
      title={resolved.title}
      // the name starts with the visible ref (label-in-name) and says what it opens
      aria-label={resolved.title ? `${refText}: ${resolved.title}` : undefined}
      onClick={(event) => {
        event.stopPropagation()
        event.preventDefault()
        resolved.open?.()
      }}
    >
      <MonoRef text={refText} />
    </button>
  ) : (
    <span className={className} title={resolved.title}>
      <MonoRef text={refText} />
    </span>
  )
}

/** The chip's look by its mark: an open card waiting on you is the blue chip; done is quieter, cancelled struck. */
export const chipClass = (view: RefView, interactive: boolean): string =>
  cn(
    "mx-px inline-flex items-center gap-1 rounded-pill px-1 py-px align-baseline font-mono text-[0.86em] leading-[1.55] font-medium whitespace-nowrap !no-underline [box-decoration-break:clone]",
    view.mark === "needs" && (view.type === "card" || view.type === "decision")
      ? "bg-needs-soft text-needs-deep"
      : "bg-fill text-ink",
    view.mark === "done" && "text-ink-2",
    view.mark === "gone" && "text-ink-2 !line-through decoration-line-control",
    interactive &&
      "cursor-pointer hover:bg-fill-hover hover:text-ink focus-visible:outline-2 focus-visible:outline-ink focus-visible:outline-offset-1",
  )

/** Keyboard focus (`:focus-visible`); a pointer's focus is not. Where the selector is unknown, any focus counts. */
const focusVisible = (el: Element): boolean => {
  try {
    return el.matches(":focus-visible")
  } catch {
    return true
  }
}

const plainClick = (event: React.MouseEvent) =>
  !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)

function PeekChip({
  refText,
  resolved,
  view,
  plain,
}: {
  refText: string
  resolved: ResolvedRef
  view: RefView
  plain: boolean
}) {
  const decide = useContext(RefDecideContext)
  const router = useRouter({ warn: false }) as ReturnType<typeof useRouter> | undefined
  const phone = usePhone()
  const anchor = useRef<HTMLAnchorElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pointer = useRef<string>("mouse")
  const [preview, setPreview] = useState(false)
  const [open, setOpen] = useState(false)
  const tipId = useId()
  const popId = useId()

  const clearTimer = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
  }, [])
  const showSoon = () => {
    clearTimer()
    if (open) return
    timer.current = setTimeout(() => setPreview(true), PREVIEW_DELAY_MS)
  }
  const hide = () => {
    clearTimer()
    setPreview(false)
  }
  useEffect(() => clearTimer, [clearTimer])

  const close = useCallback((refocus: boolean) => {
    setOpen(false)
    if (refocus) anchor.current?.focus()
  }, [])

  // a popover closes on Escape (focus back to the chip) and on a click outside it
  useEffect(() => {
    if (!open || phone) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation()
        close(true)
      }
    }
    const onDown = (event: MouseEvent) => {
      const t = event.target as Node
      if (panel.current?.contains(t) || anchor.current?.contains(t)) return
      close(false)
    }
    document.addEventListener("keydown", onKey)
    document.addEventListener("mousedown", onDown)
    return () => {
      document.removeEventListener("keydown", onKey)
      document.removeEventListener("mousedown", onDown)
    }
  }, [open, phone, close])

  const navigate = () => {
    setOpen(false)
    if (resolved.open) resolved.open()
    else if (router) router.history.push(view.href)
    else if (typeof window !== "undefined") window.location.assign(view.href)
  }

  const content = (
    <>
      <RefIcon
        type={view.type}
        variant={view.variant}
        className={view.mark === "needs" && view.type === "card" ? "text-needs" : "text-ink-2"}
      />
      <MonoRef text={shownRef(view, refText)} />
      <MarkGlyph mark={view.mark} />
    </>
  )

  if (plain)
    return (
      <span className={chipClass(view, false)} title={view.title}>
        {content}
      </span>
    )

  return (
    <>
      <a
        ref={anchor}
        href={view.href}
        data-ref-chip={refText}
        data-mark={view.mark}
        className={chipClass(view, true)}
        aria-label={chipName(view)}
        aria-describedby={preview ? tipId : undefined}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popId : undefined}
        onPointerDown={(event) => {
          pointer.current = event.pointerType
        }}
        onMouseEnter={() => {
          if (pointer.current !== "touch") showSoon()
        }}
        onMouseLeave={hide}
        onFocus={(event) => {
          // keyboard focus shows the preview; a click's focus doesn't (the popover follows)
          if (focusVisible(event.currentTarget)) showSoon()
        }}
        onBlur={hide}
        onKeyDown={(event) => {
          if (event.key === "Escape") hide()
          if (event.key === " ") {
            event.preventDefault()
            hide()
            setOpen(true)
          }
        }}
        onClick={(event) => {
          event.stopPropagation()
          // Cmd/Ctrl/Shift-click and middle-click keep the browser's own behaviour: the page, in a new tab
          if (!plainClick(event)) return
          event.preventDefault()
          hide()
          setOpen((o) => !o)
        }}
      >
        {content}
      </a>
      {preview && !open && !phone && (
        <FloatingPanel anchor={anchor.current} width={300} id={tipId} role="tooltip">
          <RefPreview view={view} />
        </FloatingPanel>
      )}
      {open && !phone && (
        <FloatingPanel
          anchor={anchor.current}
          width={view.card ? 420 : 340}
          panelRef={panel}
          id={popId}
          role="dialog"
          aria-label={`${view.type === "card" ? "Card" : view.variant} ${refText}`}
          className="max-h-[min(80dvh,640px)] overflow-y-auto"
        >
          <RefPeekBody view={view} decide={decide} onOpen={navigate} autoFocus />
        </FloatingPanel>
      )}
      {/* decision 0151: the sheet (and the card in it) mounts only while open, not with every chip on a phone */}
      {phone && open && (
        <Sheet
          open={open}
          onOpenChange={(o) => (o ? setOpen(true) : close(true))}
          title={`${view.type === "card" ? "Card" : view.variant} ${refText}`}
          side="bottom"
        >
          <div id={popId} className="pb-6">
            <RefPeekBody view={view} decide={decide} onOpen={navigate} autoFocus />
          </div>
        </Sheet>
      )}
    </>
  )
}
